import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiCalendar, FiMapPin, FiClock, FiUsers, FiArrowRight, FiTag } from 'react-icons/fi';
import { format } from 'date-fns';

/**
 * Enhanced EventCard Component
 * 
 * A professional, animated card component for displaying event information
 * with hover effects, status indicators, and responsive design.
 * Styled to match the main YouthXConnect site design.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.event - The event data to display
 * @returns {JSX.Element} - Rendered event card component
 */
const EventCard = ({ event }) => {
  // Format date values consistently
  const formatDate = (date) => {
    if (!date) return 'TBA';
    try {
      if (date.toDate) {
        // Handle Firestore Timestamp
        return format(date.toDate(), 'MMM d, yyyy');
      }
      // Handle regular Date object or string
      return format(new Date(date), 'MMM d, yyyy');
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'Invalid Date';
    }
  };

  // Determine event status
  const getEventStatus = () => {
    if (!event.eventDate) return { label: 'TBA', color: 'gray' };
    
    const now = new Date();
    const eventDate = event.eventDate.toDate ? event.eventDate.toDate() : new Date(event.eventDate);
    const lastRegDate = event.lastDateToRegister?.toDate 
      ? event.lastDateToRegister.toDate() 
      : new Date(event.lastDateToRegister || eventDate);
    
    if (eventDate < now) {
      return { label: 'Completed', color: 'gray' };
    } else if (lastRegDate < now) {
      return { label: 'Registration Closed', color: 'yellow' };
    } else {
      return { label: 'Registration Open', color: 'green' };
    }
  };

  const status = getEventStatus();
  const isPaid = event.price && event.price > 0;

  return (
    <motion.div
      whileHover={{ y: -5, transition: { duration: 0.2 } }}
      className="h-full hover-card"
    >
      <Link 
        to={`/${event.id}`} 
        className="block h-full overflow-hidden rounded-xl bg-white shadow-md transition-shadow duration-300"
      >
        {/* Card Header with Image */}
        <div className="relative">
          {/* Banner Image */}
          <div className="aspect-[16/9] bg-gray-100 overflow-hidden">
            {event.bannerImageUrl ? (
              <img
                src={event.bannerImageUrl}
                alt={event.title}
                className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "https://via.placeholder.com/800x450?text=YouthXConnect+Event";
                }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-blue-50">
                <span className="text-blue-500 font-semibold">YouthXConnect Event</span>
              </div>
            )}
          </div>
          
          {/* Status Badge */}
          <div className="absolute top-3 right-3">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
              ${status.color === 'green' 
                ? 'bg-green-100 text-green-800' 
                : status.color === 'yellow'
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-gray-100 text-gray-800'
              }`}
            >
              {status.label}
            </span>
          </div>
          
          {/* Price Badge (if applicable) */}
          {isPaid && (
            <div className="absolute bottom-3 left-3">
              <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-white/90 text-blue-800 shadow-sm backdrop-blur-sm">
                <FiTag className="mr-1" />
                ₹{event.price}
              </span>
            </div>
          )}
        </div>
        
        {/* Card Content */}
        <div className="p-5">
          <h3 className="text-lg font-semibold text-gray-900 line-clamp-2 mb-2">{event.title}</h3>
          
          {/* Event Details */}
          <div className="space-y-2 mb-4">
            {/* Date */}
            <div className="flex items-start text-sm">
              <FiCalendar className="text-blue-500 mt-0.5 mr-2 flex-shrink-0" />
              <span className="text-gray-700">{formatDate(event.eventDate)}</span>
            </div>
            
            {/* Venue */}
            {event.venue && (
              <div className="flex items-start text-sm">
                <FiMapPin className="text-blue-500 mt-0.5 mr-2 flex-shrink-0" />
                <span className="text-gray-700 line-clamp-1">{event.venue}</span>
              </div>
            )}
            
            {/* Registration Deadline */}
            {event.lastDateToRegister && (
              <div className="flex items-start text-sm">
                <FiClock className="text-blue-500 mt-0.5 mr-2 flex-shrink-0" />
                <span className="text-gray-700">
                  Register by {formatDate(event.lastDateToRegister)}
                </span>
              </div>
            )}
          </div>
          
          {/* Description */}
          {event.description && (
            <p className="text-sm text-gray-600 line-clamp-2 mb-4">
              {event.description}
            </p>
          )}
          
          {/* Card Footer */}
          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
            <div className="flex items-center text-xs text-gray-500">
              <FiUsers className="mr-1" />
              <span>Limited Spots</span>
            </div>
            <div className="text-blue-600 font-medium text-sm flex items-center">
              View Details
              <FiArrowRight className="ml-1" />
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default EventCard;
